"use client";

// Bas de la carte latérale d'une formation. Choix du 19/09/2026 : l'essai
// gratuit reste l'action principale (Believemy est gratuit au départ, comme
// Uxcel ou Coursera, et le bandeau du site le promet) ; l'achat à vie vient en
// option secondaire, prix visible ; Believemy Pro en simple lien.
//
// L'achat à vie s'ouvre formation par formation dans /admin/course/[slug]
// (149 € ou 199 €) : sans lui, la carte garde le seul bouton d'essai. Le
// paiement, comptant ou en 3 fois, passe par Stripe Checkout
// (lib/courseLifetime.js) et ouvre l'accès par la table Buyer.

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";
import { Link, useRouter } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import CoursePurchaseCelebration from "@/components/Course/CoursePurchaseCelebration/CoursePurchaseCelebration";
import BuyCourse from "@/components/cli/BuyCourse/BuyCourse";
import BillingModeSwitch from "@/components/Billing/BillingModeSwitch/BillingModeSwitch";
import CompanyBillingFields, {
    EMPTY_COMPANY,
} from "@/components/Billing/CompanyBillingFields/CompanyBillingFields";
import { formatCourseDuration } from "@/lib/courseDuration";
import { normalizePostalCode } from "@/lib/billingAddress";
import { validateCompanyBilling } from "@/lib/companyBilling";
import {
    formatDayMonth,
    formatEuros,
    installmentAmounts,
    installmentDates,
    lifetimePriceOf,
    lifetimePromotionOf,
} from "@/lib/courseLifetimePricing";
import { useCommerceSettings } from "@/contexts/CommerceSettingsContext";
import PromoCountdown from "@/components/Course/PromoCountdown/PromoCountdown";
import WithdrawalConsent from "@/components/Checkout/WithdrawalConsent";

// Poids donné à l'achat à vie. Une seule ligne à changer pour revenir en
// arrière.
//
// "une-action" (20/09/2026) : un seul bouton sur la carte, l'essai gratuit.
//     L'achat à vie devient une ligne cliquable, l'abonnement reste un lien.
//     Trois appels à l'action de poids voisins ne faisaient pas choisir : ils
//     faisaient remettre à plus tard.
// "deux-boutons" : disposition d'origine, bouton d'achat pleine largeur.
const HIERARCHIE = "une-action";

export default function CourseAccessOptions({ course }) {
    const t = useTranslations();
    const locale = useLocale();
    const router = useRouter();
    const { data: session, status } = useSession();
    // null tant qu'on ne sait pas ; sinon { paidCount, remaining, … }.
    //
    // L'avance vient de la SESSION : `refresh-user` liste les formations
    // achetées à vie, donc la possession est connue en même temps que le
    // reste du compte, sans aller-retour de plus, sur n'importe quel
    // navigateur et dès la première visite. L'appel qui suit ne sert qu'au
    // détail des échéances, et corrige si l'accès a été retiré entre-temps.
    const [owned, setOwned] = useState(() =>
        session?.user?.lifetimeCourses?.includes(course.slug) ? {} : null,
    );
    const [plansOpen, setPlansOpen] = useState(false);
    const [plan, setPlan] = useState("full");
    const [redirecting, setRedirecting] = useState(false);
    // Accès immédiat et renonciation au droit de rétractation : obligatoire
    // avant de payer (voir lib/withdrawalConsent.js).
    const [waiverChecked, setWaiverChecked] = useState(false);
    const [showWaiverError, setShowWaiverError] = useState(false);
    // Retour de Stripe : la célébration remplace la notification, qui
    // s'effaçait avant qu'on ait lu ce qui allait être prélevé ensuite.
    const [celebration, setCelebration] = useState(null);
    // Facturer une entreprise, comme dans le tunnel d'abonnement : la fiche
    // client Stripe porte l'entreprise (lib/stripeCompanyBilling.js).
    // savedCompany reste undefined tant qu'elle n'est pas lue : rien n'est
    // alors retiré côté serveur.
    const [companyEnabled, setCompanyEnabled] = useState(false);
    const [company, setCompany] = useState({
        ...EMPTY_COMPANY,
        country: "FR",
        postalCode: "",
    });
    const [savedCompany, setSavedCompany] = useState(undefined);
    const [companyError, setCompanyError] = useState(null);

    const basePrice = lifetimePriceOf(course);

    // Promotion : celle de la formation, sinon celle du site si elle est
    // ouverte aux achats à vie. Le serveur refait le même calcul au paiement,
    // c'est lui qui fait foi ; ici on ne fait qu'annoncer.
    const { settings: commerce } = useCommerceSettings();
    const [promoOver, setPromoOver] = useState(false);
    const promotion = promoOver
        ? null
        : lifetimePromotionOf(
              {
                  promoPrice: course.lifetimePromoPrice,
                  promoEndsAt: course.lifetimePromoEndsAt,
                  promoLabelFr: course.lifetimePromoLabelFr,
                  promoLabelEn: course.lifetimePromoLabelEn,
              },
              commerce,
              basePrice,
              locale,
          );
    const price = promotion ? promotion.price : basePrice;
    const hasAccess =
        session?.user?.pro ||
        session?.user?.ultimate ||
        session?.user?.currentTeam;

    // Retour de Stripe (?purchase=success) : on ouvre l'accès sans attendre
    // le webhook. Lu dans l'URL plutôt que par useSearchParams, qui ferait
    // sortir la fiche du rendu statique. Le ref garde la confirmation d'un
    // second passage de l'effet, qui ne verrait plus l'URL nettoyée.
    const confirmStarted = useRef(false);
    useEffect(() => {
        if (status !== "authenticated" || !price || hasAccess) return;
        const query = new URLSearchParams(window.location.search);
        const sessionId =
            query.get("purchase") === "success" ? query.get("session_id") : null;

        if (sessionId && !confirmStarted.current) {
            confirmStarted.current = true;

            // Stripe ne renvoie ici QUE si le paiement est passé : on félicite
            // tout de suite, sans attendre la confirmation. Elle prend
            // plusieurs secondes (appels Stripe, back, programmation des deux
            // échéances, e-mail d'équipe), pendant lesquelles on voyait la
            // fiche inchangée et on pouvait cliquer « Commencer » sans avoir
            // rien lu. Le mode de paiement vient de l'URL de retour, donc le
            // texte est juste dès la première image.
            const paidPlan =
                query.get("plan") === "installments" ? "installments" : "full";
            setCelebration({ plan: paidPlan, state: "confirming" });
            window.history.replaceState(null, "", window.location.pathname);

            fetch("/api/course/lifetime/confirm", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sessionId }),
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.owned) setOwned(data);
                    setCelebration({
                        // Le serveur fait foi sur le mode de paiement, l'URL
                        // n'était qu'une avance.
                        plan:
                            data.plan === "installments"
                                ? "installments"
                                : data.plan === "full"
                                  ? "full"
                                  : paidPlan,
                        state: data.owned ? "ready" : "pending",
                    });
                })
                // Sans réponse, le paiement a quand même eu lieu : le webhook
                // ouvrira l'accès, on le dit plutôt que de se taire.
                .catch(() =>
                    setCelebration({ plan: paidPlan, state: "pending" }),
                );
            return;
        }
        if (confirmStarted.current) return;

        let active = true;
        fetch("/api/course/lifetime/status", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ slug: course.slug }),
        })
            .then((res) => res.json())
            .then((data) => active && setOwned(data.owned ? data : null))
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [status, price, hasAccess, course.slug, t]);

    // La célébration survit à l'ouverture de l'accès, qui fait passer d'une
    // branche de rendu à l'autre. Les TROIS retours ci-dessous ont donc la
    // même forme : un fragment dont le second enfant est cette fenêtre. Sans
    // ça, React la démonte et la remonte quand `owned` bascule, et elle se
    // rejoue une seconde fois, juste après que la barre du bouton a fini de
    // se remplir.
    const celebrationModal = celebration ? (
        <CoursePurchaseCelebration
            isOpen
            onClose={() => setCelebration(null)}
            course={course}
            price={price}
            plan={celebration.plan}
            state={celebration.state}
        />
    ) : null;

    // Tant que la session n'est pas arrivée, on ne sait pas si cette
    // formation est déjà possédée : proposer de l'acheter serait faux une
    // fois sur deux. Un gabarit de la bonne hauteur, le temps d'un seul
    // aller-retour, vaut mieux qu'une offre d'achat qui disparaît.
    if (status === "loading") {
        return (
            <>
                <div className="w-full font-geistSans">
                    <div className="h-12 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
                    <div className="mt-6 h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
                </div>
                {celebrationModal}
            </>
        );
    }

    // Qui a déjà accès n'a rien à acheter : il reprend sa formation.
    if (!price || hasAccess) {
        return (
            <>
                <BuyCourse course={course} />
                {celebrationModal}
            </>
        );
    }

    // Arriver sur une formation qu'on a achetée doit se voir : sans ce bloc,
    // la fiche ressemblait à celle de n'importe qui, avec un bouton au
    // libellé changé. Et pour un paiement en trois fois, c'est ici qu'on
    // vient vérifier où en sont les prélèvements.
    if (owned) {
        return (
            <>
                <div className="w-full font-geistSans">
                    {/* Sobre : une ligne, pas un encadré de couleur. Ce qu'on
                        vient vérifier ici, c'est qu'on possède la formation,
                        pas recevoir une félicitation. */}
                    <p className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                        <SolarIcon
                            name="check-circle-bold"
                            className="shrink-0 text-base text-green-600 dark:text-green-500"
                        />
                        {t("course_access_owned_title")}
                    </p>

                    <BuyCourse
                        course={course}
                        label={t("course_access_owned_cta")}
                        owned
                    />

                    <p className="mb-0 mt-3 text-center text-xs text-gray-500 dark:text-grayDark-400">
                        {owned.remaining > 0
                            ? `${t("course_access_owned_progress", {
                                  paid: owned.paidCount || 0,
                                  total: 3,
                              })} · ${t("course_access_owned_next", {
                                  date: formatDayMonth(
                                      new Date(owned.nextDate),
                                      locale,
                                  ),
                                  amount: owned.nextAmount,
                              })}`
                            : t("course_access_owned_subtitle")}
                    </p>

                    <p className="mb-0 mt-2 text-center">
                        <Link
                            href={{
                                pathname: "/account",
                                query: { content: "purchases" },
                            }}
                            className="text-inherit"
                        >
                            <span className="text-xs text-gray-500 underline underline-offset-4 transition-colors hover:text-gray-900 dark:text-grayDark-400 dark:hover:text-white">
                                {t("course_access_owned_link")}
                            </span>
                        </Link>
                    </p>
                </div>
                {celebrationModal}
            </>
        );
    }

    const amounts = installmentAmounts(price);
    // Durée annoncée par la fiche (champ marketing), jamais recalculée.
    const courseDuration = formatCourseDuration(course, t);
    const [secondDate, thirdDate] = installmentDates();

    // Entreprise déjà enregistrée (Mon compte, abonnement précédent) : la
    // fenêtre la retrouve, interrupteur activé.
    const loadSavedCompany = () => {
        fetch("/api/user/billing-company")
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
            .then((data) => {
                setSavedCompany(data.company || null);
                if (data.company) {
                    setCompany({ ...EMPTY_COMPANY, ...data.company });
                    setCompanyEnabled(true);
                } else if (session?.user?.addressCountry) {
                    setCompany((current) => ({
                        ...current,
                        country: session.user.addressCountry,
                    }));
                }
            })
            .catch(() => {});
    };

    const openPlans = () => {
        if (!session) {
            router.push(
                `/signup?courseImage=${course.image}&courseSlug=${course.slug}`,
            );
            return;
        }
        if (savedCompany === undefined) loadSavedCompany();
        setPlansOpen(true);
    };

    const checkout = async () => {
        if (redirecting) return;
        if (!waiverChecked) {
            setShowWaiverError(true);
            return;
        }

        // Entreprise : l'objet la pose, null la retire (seulement s'il y en
        // avait une), absent ne change rien. Mêmes règles que le serveur.
        let companyForServer;
        if (companyEnabled) {
            const checked = validateCompanyBilling({
                ...company,
                postalCode: normalizePostalCode(company.country, company.postalCode),
            });
            if (!checked.ok) {
                setCompanyError(t(checked.errorKey));
                return;
            }
            companyForServer = checked.company;
        } else if (savedCompany) {
            companyForServer = null;
        }

        setCompanyError(null);
        setRedirecting(true);
        try {
            const res = await fetch("/api/course/lifetime/checkout", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    slug: course.slug,
                    plan,
                    locale,
                    company: companyForServer,
                    withdrawalWaiver: true,
                }),
            });
            const data = await res.json();
            if (res.ok && data.url) {
                window.location.assign(data.url);
                return;
            }
            if (data.field === "company") {
                setCompanyError(data.message);
            } else {
                toast.error(data.message || t("course_lifetime_error_generic"));
            }
        } catch {
            toast.error(t("course_lifetime_error_generic"));
        }
        setRedirecting(false);
    };

    // Deux tuiles : le montant domine, la mécanique se lit en dessous. Une
    // échéance datée rassure mieux qu'un « 3 × » abstrait.
    const plans = [
        {
            id: "full",
            amount: `${price}\u00a0€`,
            detail: t("course_access_plan_full_short"),
        },
        {
            id: "installments",
            amount: t("course_access_plan_installments_amount", {
                amount: formatEuros(amounts[0], locale),
            }),
            detail: t("course_access_plan_installments_short", {
                secondDate: formatDayMonth(secondDate, locale),
                thirdDate: formatDayMonth(thirdDate, locale),
            }),
        },
    ];

    // Ce qui part du compte aujourd'hui, et rien d'autre : c'est ce que le
    // bouton doit annoncer.
    // formatEuros ne rend que le nombre : le symbole est ajouté par les
    // clés de traduction, il faut donc le poser ici aussi.
    const dueToday =
        plan === "installments"
            ? `${formatEuros(amounts[0], locale)}\u00a0€`
            : `${price}\u00a0€`;

    const promises = [
        {
            icon: "infinity-linear",
            strong: t("course_access_promise_lifetime_title"),
            rest: t("course_access_promise_lifetime_text"),
        },
        {
            icon: "refresh-circle-linear",
            strong: t("course_access_promise_version_title"),
            rest: t("course_access_promise_version_text"),
        },
        // CGV : la garantie de 30 jours de l'achat à vie est l'article 15 des
        // CGV, et ses modalités de paiement l'article 9
        // (app/[locale]/(app)/terms-of-sale). Toute modification doit y être
        // reportée.
        {
            icon: "shield-check-linear",
            strong: t("course_access_promise_refund_title"),
            rest: t("course_access_promise_refund_text"),
        },
    ];

    return (
        <>
            <div className="w-full font-geistSans">
                <BuyCourse course={course} />

                <div className="my-6 flex items-center gap-3 text-xs text-gray-400 dark:text-grayDark-500">
                    <span className="h-px flex-1 bg-gray-200 dark:bg-borderDark" />
                    {t("course_access_or")}
                    <span className="h-px flex-1 bg-gray-200 dark:bg-borderDark" />
                </div>

                {HIERARCHIE === "une-action" ? (
                    // Une ligne, pas un bouton : l'achat à vie reste lisible et
                    // chiffré, sans disputer le regard à l'essai gratuit.
                    //
                    // En promotion (23/09/2026), la ligne se TEINTE au lieu de
                    // grossir : bordure orange pâle, remise en pastille contre
                    // le titre, compte à rebours sur une seule ligne. L'offre
                    // se remarque au premier coup d'œil sans reprendre le
                    // premier rôle à l'essai gratuit.
                    <button
                        type="button"
                        onClick={openPlans}
                        className={`group flex w-full items-start justify-between gap-4 rounded-xl border px-4 py-3 text-left transition-colors ${
                            promotion
                                ? "border-excitationOrange/40 bg-excitationOrange/[0.04] hover:border-excitationOrange/70 dark:border-excitationOrange/40 dark:bg-excitationOrange/[0.07] dark:hover:border-excitationOrange/70"
                                : "border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20"
                        }`}
                    >
                        <span className="min-w-0">
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="font-medium text-gray-900 dark:text-white">
                                    {t("course_access_lifetime_title")}
                                </span>
                                {promotion && (
                                    // La remise en chiffres, contre le titre :
                                    // c'est elle qui fait la différence avec
                                    // une carte ordinaire, et elle se lit avant
                                    // le prix barré.
                                    <span className="rounded-full bg-excitationOrange px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                                        {t("course_access_promo_badge", {
                                            percent: promotion.percent,
                                        })}
                                    </span>
                                )}
                            </span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-grayDark-400">
                                {t("course_access_installments", {
                                    amount: formatEuros(amounts[0], locale),
                                })}
                            </span>
                            {promotion && (
                                // Le temps en orange, le nom de l'offre en
                                // gris : sans cette différence, tout se valait
                                // et la carte devenait un pavé de couleur.
                                //
                                // Le nom s'affiche ENTIER (23/09/2026) : coupé
                                // à « Offre… », il ne disait plus rien, et
                                // c'est lui qui donne sa raison à la remise.
                                // Le compte à rebours ne se coupe jamais (il
                                // est insécable), et le point médian reste
                                // collé au premier mot du nom : la coupure
                                // tombe donc avant lui, jamais après.
                                <span className="mt-1.5 flex items-start gap-1.5 text-xs leading-snug">
                                    <SolarIcon
                                        name="alarm-linear"
                                        className="mt-px shrink-0 text-sm text-excitationOrange"
                                    />
                                    <span className="min-w-0">
                                        <span className="font-semibold text-excitationOrange">
                                            <PromoCountdown
                                                endsAt={promotion.endsAt}
                                                onExpire={() =>
                                                    setPromoOver(true)
                                                }
                                            />
                                        </span>
                                        {promotion.label && (
                                            <span className="text-gray-500 dark:text-grayDark-400">
                                                {" \u00b7\u00a0"}
                                                {promotion.label}
                                            </span>
                                        )}
                                    </span>
                                </span>
                            )}
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap font-semibold text-gray-900 dark:text-white">
                            {promotion && (
                                <span className="text-sm font-normal text-gray-400 line-through dark:text-grayDark-500">
                                    {basePrice}&nbsp;€
                                </span>
                            )}
                            {price}&nbsp;€
                            <SolarIcon
                                name="alt-arrow-right-linear"
                                className="text-sm transition-transform group-hover:translate-x-0.5"
                            />
                        </span>
                    </button>
                ) : (
                    <>
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="mb-0 font-semibold text-gray-900 dark:text-white">
                                    {t("course_access_lifetime_title")}
                                </p>
                                <p className="mb-0 mt-0.5 text-sm text-gray-500 dark:text-grayDark-400">
                                    {t("course_access_installments", {
                                        amount: formatEuros(amounts[0], locale),
                                    })}
                                </p>
                            </div>
                            <p className="mb-0 shrink-0 whitespace-nowrap text-xl font-bold leading-tight text-gray-900 dark:text-white">
                                {price}&nbsp;€
                            </p>
                        </div>

                        <div className="mt-4">
                            <Button type="secondary" block onClick={openPlans}>
                                {t("course_access_lifetime_cta")}
                            </Button>
                        </div>
                    </>
                )}
                <p className="mb-0 mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-grayDark-400">
                    <SolarIcon name="shield-check-linear" className="text-sm" />
                    {t("course_access_guarantee")}
                </p>

                {/* Lien-bouton : la couleur va sur le span, jamais sur le <a>
                    (a:hover le repeindrait en rose, cf. globals.css). */}
                <Link href="/pricing" className="group mt-6 block text-center">
                    <span className="block text-sm text-gray-500 transition-colors group-hover:text-gray-900 dark:text-grayDark-400 dark:group-hover:text-white">
                        {t("course_access_pro_link", {
                            count: process.env.NUMBER_OF_COURSES,
                        })}
                    </span>
                    <span className="mt-0.5 block text-sm font-medium text-gray-900 dark:text-white">
                        {t("course_access_pro_link_price", {
                            price: process.env.BELIEVEMY_PREMIUM_ANNUALLY_PRICE,
                        })}
                        <SolarIcon
                            name="alt-arrow-right-linear"
                            className="ml-1 inline-block align-[-2px] text-sm transition-transform group-hover:translate-x-0.5"
                        />
                    </span>
                </Link>

                <BottomSheetModal
                    isOpen={plansOpen}
                    onClose={() => !redirecting && setPlansOpen(false)}
                    labelledBy="course-lifetime-plans-title"
                    // Plus large que le défaut : deux tuiles de paiement côte à
                    // côte et, en mode entreprise, un formulaire d'adresse
                    // complet. À 448 px tout se serrait.
                    maxWidthClass="sm:max-w-xl"
                    portal
                >
                    <div className="p-6 font-geistSans sm:p-8">
                        {/* Ce qu'on achète. C'est un achat de 149 €, il doit en
                            avoir l'air : la formation d'abord, le prix ensuite,
                            la mécanique de paiement en dernier. */}
                        <div className="flex items-center gap-3">
                            <div
                                className="h-12 w-12 flex-none rounded-xl bg-gray-100 bg-cover bg-center dark:bg-white/5"
                                style={
                                    course.image
                                        ? { backgroundImage: `url(${course.image})` }
                                        : undefined
                                }
                            />
                            <div className="min-w-0">
                                <h2
                                    id="course-lifetime-plans-title"
                                    className="mb-0 truncate text-[15px] font-semibold text-gray-900 dark:text-white"
                                >
                                    {course.name}
                                </h2>
                                {courseDuration && (
                                    <p className="mb-0 mt-0.5 text-[13px] text-gray-500 dark:text-grayDark-400">
                                        {courseDuration}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            {promotion && (
                                <span className="whitespace-nowrap text-2xl font-semibold text-gray-400 line-through dark:text-grayDark-500">
                                    {basePrice}&nbsp;€
                                </span>
                            )}
                            <span className="whitespace-nowrap text-[40px] font-bold leading-none tracking-tight text-gray-900 dark:text-white">
                                {price}&nbsp;€
                            </span>
                            {promotion ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-excitationOrange/10 px-2.5 py-0.5 text-sm font-semibold text-excitationOrange">
                                    {t("course_access_promo_badge", {
                                        percent: promotion.percent,
                                    })}
                                    {promotion.label && (
                                        <span className="font-medium">
                                            {promotion.label}
                                        </span>
                                    )}
                                </span>
                            ) : (
                                <span className="text-sm text-gray-500 dark:text-grayDark-400">
                                    {t("course_access_price_note")}
                                </span>
                            )}
                        </div>

                        {promotion && (
                            <p className="mb-0 mt-2 text-sm font-medium text-excitationOrange">
                                <PromoCountdown
                                    endsAt={promotion.endsAt}
                                    onExpire={() => setPromoOver(true)}
                                    icon
                                />
                            </p>
                        )}

                        {/* Trois lignes, aucun cadre : ce sont les bordures qui
                            alourdissaient, pas le nombre de promesses. */}
                        <ul className="mb-0 mt-6 list-none space-y-3 p-0">
                            {promises.map((promise) => (
                                <li
                                    key={promise.icon}
                                    className="flex items-start gap-2.5"
                                >
                                    <SolarIcon
                                        name={promise.icon}
                                        className="mt-0.5 shrink-0 text-lg text-excitationOrange"
                                    />
                                    <span className="text-sm leading-snug text-gray-600 dark:text-grayDark-400">
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                            {promise.strong}
                                        </span>{" "}
                                        {promise.rest}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <hr className="my-6 border-gray-100 dark:border-white/10" />

                        <p
                            id="course-lifetime-plans-label"
                            className="mb-3 text-[13px] font-medium text-gray-900 dark:text-white"
                        >
                            {t("course_access_plan_title")}
                        </p>
                        <div
                            role="radiogroup"
                            aria-labelledby="course-lifetime-plans-label"
                            className="grid grid-cols-2 gap-2.5"
                        >
                            {plans.map((option) => {
                                const selected = plan === option.id;
                                return (
                                    <label
                                        key={option.id}
                                        className={`relative cursor-pointer rounded-2xl border-2 p-4 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-gray-900/20 dark:has-[:focus-visible]:ring-white/30 ${
                                            selected
                                                ? "border-gray-900 dark:border-white"
                                                : "border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/25"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="course-lifetime-plan"
                                            value={option.id}
                                            checked={selected}
                                            onChange={() => setPlan(option.id)}
                                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                        />
                                        <span className="block pr-6 text-lg font-bold leading-tight text-gray-900 dark:text-white">
                                            {option.amount}
                                        </span>
                                        <span className="mt-1 block text-xs leading-snug text-gray-500 dark:text-grayDark-400">
                                            {option.detail}
                                        </span>
                                        <span
                                            className={`absolute right-3 top-3 flex h-4 w-4 items-center justify-center rounded-full border transition-colors ${
                                                selected
                                                    ? "border-gray-900 bg-gray-900 dark:border-white dark:bg-white"
                                                    : "border-gray-300 dark:border-white/30"
                                            }`}
                                        >
                                            {selected && (
                                                <span className="h-1.5 w-1.5 rounded-full bg-white dark:bg-bgDark-800" />
                                            )}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>

                        {/* Une entreprise qui paie la formation : sa facture (et
                            celles des échéances) est établie à son nom */}
                        <div className="mt-5">
                            <BillingModeSwitch
                                company={companyEnabled}
                                onChange={(isCompany) => {
                                    setCompanyEnabled(isCompany);
                                    setCompanyError(null);
                                }}
                            />
                        </div>
                        {companyEnabled && (
                            <div className="mt-4">
                                <CompanyBillingFields
                                    idPrefix="course-lifetime-company"
                                    value={company}
                                    onChange={(patch) => {
                                        setCompany((current) => ({
                                            ...current,
                                            ...patch,
                                        }));
                                        setCompanyError(null);
                                    }}
                                    labelClassName="label"
                                    inputClassName="input"
                                />
                            </div>
                        )}
                        {companyError && (
                            <p
                                className="mb-0 mt-4 text-sm text-red-500 dark:text-red-400"
                                role="alert"
                            >
                                {companyError}
                            </p>
                        )}

                        <WithdrawalConsent
                            className="mt-6"
                            consentKey="checkout_withdrawal_consent_course"
                            checked={waiverChecked}
                            onChange={(checked) => {
                                setWaiverChecked(checked);
                                if (checked) setShowWaiverError(false);
                            }}
                            showError={showWaiverError}
                        />

                        <div className="mt-5">
                            <Button
                                type="primary"
                                block
                                onClick={checkout}
                                isLoading={redirecting}
                                progress
                            >
                                {t("course_access_pay_cta", { amount: dueToday })}
                            </Button>
                        </div>
                        <p className="mb-0 mt-3 text-center text-xs text-gray-400 dark:text-grayDark-500">
                            {t("course_access_secure_note")}
                        </p>
                    </div>
                </BottomSheetModal>
            </div>
            {celebrationModal}
        </>
    );
}
